import React, { useEffect, useMemo, useState } from 'react';
import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';
import { Card } from 'primereact/card';
import './Settings.scss';
import { FormField } from '../../components/FormField/FormField';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { ReservationTypeWithColor, useReservationTypes } from '../../hooks/AppData/useReservationTypes';
import { BranchesSelect } from '../../components/BranchesSelect/BranchesSelect';
import { HolidaysDialog } from '../../components/HolidaysDialog/HolidaysDialog';
import { ALL_FEATURES } from '../../config/features';
import { useFeaturesToggle, useOrganizationFeaturesToggle } from '../../hooks/useFeaturesToggle';
import { Divider } from 'primereact/divider';
import { SIGNED_DOCUMENT_TYPES } from '../../types/signedDocument';
import { CopyHoursDialog } from '../../components/CopyHoursDialog/CopyHoursDialog';
import { SettingsSkeleton } from './SettingsSkeleton';
import { getBranchFromId, getBranchReservationTypes } from './utils/branches';
import { useBranchSettingsForm } from './hooks/useBranchSettingsForm';
import { BranchSettingsSection } from './sections/BranchSettingsSection';
import { useReservationHoursForm } from './hooks/useReservationHoursForm';
import { ReservationHoursSection } from './sections/ReservationHoursSection';
import { useOrgEmailSettingsForm } from './hooks/useOrgEmailSettingsForm';
import { OrgEmailSettingsSection } from './sections/OrgEmailSettingsSection';

export const Settings = () => {
  const { organization, organizationLoading, orgBranches, mutateOrganization } = useOrganizationContext();
  const [selectedBranchId, setSelectedBranchId] = useState<string>();
  const [showHolidaysDialog, setShowHolidaysDialog] = useState(false);
  const [showCopyHoursDialog, setShowCopyHoursDialog] = useState(false);
  const [selectedReservationType, setSelectedReservationType] = useState<ReservationTypeWithColor>();

  const userHasEditFeature = useFeaturesToggle([ALL_FEATURES.BUSINESS_CONFIG_WRITE]);
  const orgHasAnySignedDocumentsFeature = useOrganizationFeaturesToggle(Object.values(SIGNED_DOCUMENT_TYPES));
  const selectedBranch = useMemo(() => getBranchFromId(selectedBranchId, orgBranches), [selectedBranchId, orgBranches]);
  const { reservationTypes, mutateReservationTypes, reservationTypesLoading } = useReservationTypes();
  const branchReservationTypes = useMemo(() => {
    return getBranchReservationTypes(selectedBranchId, reservationTypes);
  }, [selectedBranchId, reservationTypes]);
  const reservationTypesWithRestrictedDates = useMemo(
    () => branchReservationTypes?.filter((_) => !_.allowAnyDate),
    [branchReservationTypes],
  );

  useEffect(() => {
    // TODO We should probably just get the first branch, and if it hasnt got reservation types,
    // dont show the reservation hours section, or show a message saying that there are no reservation types
    if (!selectedBranchId && reservationTypes && orgBranches?.length) {
      const firstBranchWithReservationTypes = orgBranches.find((_) => getBranchReservationTypes(_.id, reservationTypes)?.length);
      setSelectedBranchId(firstBranchWithReservationTypes?.id);
    }
  }, [orgBranches, selectedBranchId, reservationTypes]);

  const branchSettings = useBranchSettingsForm({
    selectedBranch,
    selectedBranchId,
    orgHasAnySignedDocumentsFeature,
    mutateOrganization,
  });

  const reservationHours = useReservationHoursForm({
    selectedBranchId,
    selectedBranch,
    branchReservationTypes,
    reservationTypes,
    mutateReservationTypes,
    selectedReservationType,
  });

  const orgEmailSettings = useOrgEmailSettingsForm({ organization, mutateOrganization });

  const handleOpenHolidaysDialog = (reservationType: ReservationTypeWithColor) => {
    setSelectedReservationType(reservationType);
    setShowHolidaysDialog(true);
  };

  const handleOpenCopyHoursDialog = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCopyHoursDialog(true);
  };

  const handleCloseCopyHoursDialog = () => {
    setShowCopyHoursDialog(false);
  };

  if (organizationLoading || reservationTypesLoading) {
    return <SettingsSkeleton />;
  }

  return (
    <PageTemplate className='settings-page' title='Configuración de sucursales'>
      <Card className='branch-settings-card'>
        <FormField
          helper='La configuración se aplicará a la sucursal seleccionada'
          className='settings-branch-select'
          elementId='config-branch'
          labelTitle='Sucursal'
        >
          <BranchesSelect
            id='config-branch'
            isSingle
            value={selectedBranchId}
            onChange={(branch) => branch && setSelectedBranchId(branch.id)}
          />
        </FormField>
        <Divider />
        <BranchSettingsSection
          branchSettings={branchSettings}
          organizationLoading={organizationLoading}
          userHasEditFeature={userHasEditFeature}
          orgHasAnySignedDocumentsFeature={orgHasAnySignedDocumentsFeature}
        />
        <Divider />
        <ReservationHoursSection
          reservationHours={reservationHours}
          reservationTypesWithRestrictedDates={reservationTypesWithRestrictedDates}
          orgBranches={orgBranches}
          selectedBranchId={selectedBranchId}
          organizationLoading={organizationLoading}
          userHasEditFeature={userHasEditFeature}
          onOpenCopyHoursDialog={handleOpenCopyHoursDialog}
          onOpenHolidaysDialog={handleOpenHolidaysDialog}
        />
      </Card>
      <h3>Configuración de la empresa</h3>
      <OrgEmailSettingsSection
        orgEmailSettings={orgEmailSettings}
        organization={organization}
        organizationLoading={organizationLoading}
        userHasEditFeature={userHasEditFeature}
      />
      <HolidaysDialog
        visible={showHolidaysDialog}
        onHide={() => setShowHolidaysDialog(false)}
        value={selectedReservationType ? (reservationHours.form?.[selectedReservationType.id]?.closedDays ?? []) : []}
        onChange={reservationHours.handleHolidaysChange}
        title={`Días de cierre - ${selectedReservationType?.name ?? ''}`}
        otherReservationTypes={branchReservationTypes?.filter((rt) => rt.id !== selectedReservationType?.id && !rt.allowAnyDate)}
        reservationHours={reservationHours.form}
      />
      <CopyHoursDialog
        visible={showCopyHoursDialog}
        onHide={handleCloseCopyHoursDialog}
        onCopy={reservationHours.handleCopyHours}
        currentBranchId={selectedBranchId ?? ''}
      />
    </PageTemplate>
  );
};
